
import {
  Component, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { debounce } from 'ts-debounce';
import moment from 'moment';
import FiltersModal from '@/features/common/components/servicesFilter/FiltersModal.vue';
import DateRangePickerInput from '@/features/common/components/DateRangePickerInput.vue';
import { SelectedUnscheduledPickup, UnscheduledPickups } from '@/features/schedule-pickup/types';
import { getUnscheduledPickupsService } from '@/features/schedule-pickup/services/schedulePickup.services';
import { defaultOffsetLimit } from '@/features/common/raw';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import { deleteShipment } from '@/features/shipment/helpers/createShipment';
import PendingTable from './PendingTable.vue';

@Component({
  components: {
    PendingTable,
    FiltersModal,
    DateRangePickerInput,
  },
})
export default class PendingShipments extends Mixins(ResponsivenessMixin) {
  @Prop({ required: true }) selectedPickups!: SelectedUnscheduledPickup[];

  private pendingTableKey = 1;

  private showFilters = false;

  private selectedCarriers: string[] = [];

  // * uncomment this when service level in filters is also included
  // private selectedServiceLevels: string[] = [];

  private order = true;

  private dateSortingActive = false;

  private searchField = '';

  private isLoading = false;

  private isLoadMoreHint = true;

  private isSelectAll = false;

  private isOnlyMyShipment = false;

  private isTodaysShipment = false;

  private dateRangeInput = '';

  private dateCalendar: string[] = [];

  private unscheduledPickups: UnscheduledPickups[] = [];

  // * uncomment this when service level in filters is also included
  // get filteredUnscheduledPickups(): UnscheduledPickups[] {
  //   return this.filterShipmentsByServiceLevel(this.unscheduledPickups);
  // }

  @Watch('unscheduledPickups.length')
  private togglePickupActionFooter(len: number) {
    this.$emit('togglePickupFooter', !!len);
  }

  @Watch('selectedPickups.length')
  private checkOrUncheckSelectAllPickups(selectedPickupsLength: number) {
    this.isSelectAll = selectedPickupsLength === this.unscheduledPickups.length;
  }

  @Watch('isTodaysShipment')
  private resetDateRangeFilter(isTodaysShipment: boolean) {
    if (!isTodaysShipment) return;

    this.dateRangeInput = '';
    this.dateCalendar = [];
  }

  async created(): Promise<void> {
    this.isLoading = true;

    const items = await getUnscheduledPickupsService();
    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.unscheduledPickups = items;
    }

    this.isLoading = false;
  }

  // * uncomment this when service level in filters is also included
  // private filterShipmentsByServiceLevel(
  //   shipments: UnscheduledPickups[],
  // ): UnscheduledPickups[] {
  //   if (!this.selectedServiceLevels.length) return shipments;

  //   return shipments.filter(
  //     (item) => this.selectedServiceLevels.includes(
  //       item.selectedService,
  //     ),
  //   );
  // }

  private deleteShipment(trackingNumber: string): void {
    const onSuccess = () => {
      this.unscheduledPickups = this.unscheduledPickups.filter(
        (item) => {
          return item.trackingNumber !== trackingNumber;
        },
      );

      this.pendingTableKey += 1;

      showSuccessAlertDialog(this.$t('shipment.alertDialog.shipmentDeleted') as string);
    };

    deleteShipment(trackingNumber, onSuccess, {
      content: this.$t('shipment.deleteShipmentPrompt') as string,
      btnText: this.$t('buttons.deleteShipment') as string,
    });
  }

  private onUpdateSelectedPickup(isChecked: boolean, pickupItem: SelectedUnscheduledPickup): void {
    let pickupItems = this.selectedPickups;
    if (isChecked) {
      pickupItems.push(pickupItem);
    } else {
      pickupItems = pickupItems.filter((item) => item.index !== pickupItem.index);
    }
    this.$emit('updateSelectedPickups', pickupItems);
  }

  private onSelectAllPickups(isSelected: boolean) {
    const pickupItems = isSelected
      ? this.unscheduledPickups.map((pickup, index) => {
        return {
          ...pickup,
          index,
        };
      })
      : [];
    this.$emit('updateSelectedPickups', pickupItems);
  }

  private requestCallCount = 1;

  private async fetchMoreUnScheduledPickup() {
    this.isLoading = true;

    const unscheduledPickupItems = await getUnscheduledPickupsService({
      offset: defaultOffsetLimit * this.requestCallCount,
      search: this.searchField,
      carriers: this.selectedCarriers,
      dateRange: this.getDateRangeData(),
      orderByDesc: this.order,
      isDontShowSharedUsers: this.isOnlyMyShipment,
    });

    if (unscheduledPickupItems && unscheduledPickupItems.length) {
      this.unscheduledPickups = [...this.unscheduledPickups, ...unscheduledPickupItems];
      this.requestCallCount += 1;
    }

    this.isLoadMoreHint = !!unscheduledPickupItems && unscheduledPickupItems.length > 0;
    this.isLoading = false;
  }

  private searchPendingShipments(value: string) {
    this.debounceSearch(value);
  }

  private debounceSearch = debounce(this.debounceSearchHandler, 500);

  private async debounceSearchHandler(value: string) {
    this.isLoading = true;
    this.requestCallCount = 1;

    const items = await getUnscheduledPickupsService({
      search: value,
      carriers: this.selectedCarriers,
      dateRange: this.getDateRangeData(),
      orderByDesc: this.order,
      isDontShowSharedUsers: this.isOnlyMyShipment,
    });

    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.isLoadMoreHint = !!items && items.length > 20;
      this.unscheduledPickups = items;
      this.pendingTableKey += 1;
    }

    this.isLoading = false;
  }

  private getDateRangeData() {
    if (!this.isTodaysShipment) return this.dateCalendar;

    const todayDate = moment().local().format('YYYY-MM-DD');
    return [todayDate, todayDate];
  }

  private async sortByCreatedDate() {
    this.order = !this.order;
    this.dateSortingActive = true;
    this.fetchUnscheduledShipments();
  }

  private async applyFilters(): Promise<void> {
    this.showFilters = false;
    this.fetchUnscheduledShipments();
  }

  private applyDateRangeFilter() {
    this.fetchUnscheduledShipments();
  }

  private fetchUnscheduledShipments() {
    this.debounceSearchHandler(this.searchField);
  }
}
